<template>
    <div>
        <rkm-row>
            <rkm-col>
                <rkm-data-table
                    :fields="fields"
                    :repository="repositories.paciente"
                    :disabled-button-search="true"
                    :tbody-tr-class="rowClass"
                    ref="DataTable"
                    sort-by="nome"
                >
                    <template v-slot:cell(nome)="{ item }">
                        {{ item.nome }}

                        <template v-if="item.nomeSocial">
                            <template v-if="item.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>({{ item.nomeSocial }})</strong>
                            </template>

                            <template v-else>
                                ({{ item.nomeSocial }})
                            </template>
                        </template>
                    </template>

                    <template v-slot:cell(dataNascimento)="row">
                        {{ row.value | formatDate }}
                    </template>

                    <template v-slot:cell(cpf)="row">
                        {{ row.value | formatCpf }}
                    </template>

                    <template v-slot:cell(cns)="row">
                        {{ row.value | formatCns }}
                    </template>

                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            @click.stop="search(item)"
                            color="primary"
                            title="Visualizar"
                            small
                        >
                            <rkm-icon icon="search" />
                        </rkm-button>
                    </template>
                </rkm-data-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    Linhas em <span class="danger">vermelho</span> representam pacientes em óbito.<br>
                    Linhas em <span class="warning">amarelo</span> representam pacientes sem CNS e CPF.
                </p>
            </rkm-col>
        </rkm-row>

        <ModalAgendamentoExameLaboratorial
            v-if="pacienteId"
            :paciente-id="pacienteId"
            :hasPrint="true"
            :hasAmbulatorio="hasAmbulatorio"
            @save="handleAgendado"
            hide-agendamentos
            ref="ModalAgendamentoExameLaboratorial"
        />
    </div>
</template>

<script>

// Components.
import ModalAgendamentoExameLaboratorial from '@/components/exame-laboratorial/agendamento-exame/Modal'

// Mixins.
import { repository, error, printExameLaboratorial, stringCase } from '@/mixins'

// Filters.
import formats from '@/filters/formats'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import impressaoAgendamento from '@/enums/exame-laboratorial/impressaoAgendamento'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {
        ModalAgendamentoExameLaboratorial,
    },
    created() {

        this.repositories = {
            paciente: this.getConectaSusRepository('cadastro.sujeitoAtencao.paciente'),
            parametrizacao: this.getConectaSusRepository('cadastro.parametrizacao'),
        }
    },
    data: () => ({
        form: {},
        pacienteId: null,
        fields: [
            {
                name: 'cadastroCidadao.nome',
                key: 'nome',
                label: 'Nome (Nome social)',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeSocial',
                key: 'nomeSocial',
                label: 'Nome social',
                searchable: true,
                sortable: true,
                visible: false
            },
            {
                name: 'cadastroCidadao.dataNascimento',
                key: 'dataNascimento',
                label: 'Data de nascimento',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.nomeMae',
                key: 'nomeMae',
                label: 'Nome da mãe',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroPaciente.prontuario',
                key: 'prontuario',
                label: 'Prontuário municipal',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cpf',
                key: 'cpf',
                label: 'CPF',
                searchable: true,
                sortable: true
            },
            {
                name: 'cadastroCidadao.cns',
                key: 'cns',
                label: 'CNS',
                searchable: true,
                sortable: true
            },
            {
                name: 'actions',
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        repositories: {
            paciente: null,
        },
        enums: {
            preferenciaNome,
            classificacao,
            impressaoAgendamento,
            subclassificacao,
        }
    }),
    filters: {
        formatDate: formats.formatDate,
        formatCns: formats.formatCns,
        formatCpf: formats.formatCpf,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        rowClass(item, type) {
            if(item && type === 'row') {
                if (item.obitoEm) {
                    return 'table-danger'
                }
                else if (!item.cns && !item.cpf) {
                    return 'table-warning'
                }
            }
        },
        search(item) {

            this.setLoading()

            this.repositories.paciente.validCNS(this.camelObjectKeysToSnakeRecursive({pacienteId: item.id}))
                .then(response => {

                    if (item.subclassificacaoId === this.enums.subclassificacao.TEMPORARIO
                        && response.data.additionals.avisaCnsTemporarioInvalido) {

                            const confirm = async () => {

                                this.redirect(item)

                                return false
                            }

                            const msg = `Paciente com cadastro temporário. Deseja continuar?`

                            this.showConfirmation(msg, confirm)

                            return false
                    }

                    if (item.classificacaoId !== this.enums.classificacao.MUNICIPE
                        && item.classificacaoId !== this.enums.classificacao.PROVISORIO
                        && item.classificacaoId !== this.enums.classificacao.OUTRO_MUNICIPIO) {

                            this.showValidationNotification(`Não é permitido realizar agendamento de exames para a
                                classificação do paciente.`)

                            return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.PROVISORIO) {

                        const confirm = async () => {

                            this.redirect(item)

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            const msg = `Paciente tem classificação provisória e com cadastro desatualizado há
                                <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Apenas os exames parametrizados para
                                pacientes provisórios serão listados. Deseja continuar?`

                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente tem classificação provisória. Apenas os exames parametrizados para pacientes
                                provisórios serão listados. Deseja continuar?`

                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    if (item.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {

                        const confirm = async () => {

                            this.redirect(item)

                            return false
                        }

                        if (item.diasCadastroDesatualizado > 0) {

                            const msg = `Paciente é de outro município e com cadastro desatualizado há
                                <strong>${item.diasCadastroDesatualizado}</strong> dia(s). Apenas os exames parametrizados para
                                pacientes de outros municípios serão listados. Deseja continuar?`

                            this.showConfirmation(msg, confirm)
                        } else {

                            const msg = `Paciente é de outro município. Apenas os exames parametrizados para pacientes de
                                outros municípios serão listados. Deseja continuar?`

                            this.showConfirmation(msg, confirm)
                        }

                        return false
                    }

                    if (item.diasCadastroDesatualizado > 0) {

                        const confirm = async () => {

                            this.redirect(item)

                            return false
                        }

                        const msg = `Paciente com cadastro desatualizado há <strong>${item.diasCadastroDesatualizado}</strong>
                            dia(s). Deseja continuar?`

                        this.showConfirmation(msg, confirm)

                        return false
                    }

                    this.redirect(item)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleEtiquetaPrint() {

            let etiqueta = {
                pacienteNome: this.item.pacienteNome,
                pacienteNomeSocial: this.item.pacienteNomeSocial,
                agendamentoNumero: this.item.agendamentoNumero,
                unidadeNome: this.item.unidade,
                laboratorioNome: this.item.laboratorio,
                idade: this.item.idade,
                prontuario: this.item.prontuario,
                sistemaNome: this.item.sistemaNome,
                sistemaVersao: this.item.sistemaVersao,
                nomePrefeitura: this.item.nomePrefeitura,
            }

            this.print().etiqueta(etiqueta)
        },
        handlePrint() {

            let agendamento = {
                pacienteNome: this.item.pacienteNome,
                pacienteNomeSocial: this.item.pacienteNomeSocial,
                prontuario: this.item.prontuario,
                cpf: formats.formatCpf(this.item.cpf),
                nomeResponsavel: this.item.nomeResponsavel,
                dataNascimento: this.item.dataNascimento,
                agendamentoNumero: this.item.agendamentoNumero,
                medicoNome: this.item.medicoNome,
                data: this.item.data,
                examesIncluido: this.item.examesIncluido,
                horaExame: this.item.horaExame,
                unidade: this.item.unidade,
                observacao: this.item.observacao,
                endereco: this.item.endereco,
                numeroEndereco: this.item.numeroEndereco,
                bairro: this.item.bairro,
                telefone: this.item.telefone,
                tipo: this.item.tipo,
                cep: this.item.cep,
                localColeta: this.item.localColeta,
                enderecoColeta: this.item.enderecoColeta,
                numeroEnderecoColeta: this.item.numeroEnderecoColeta,
                bairroColeta: this.item.bairroColeta,
                tipoColeta: this.item.tipoColeta,
                cepColeta: this.item.cepColeta,
                telefoneColeta: this.item.telefoneColeta,
                laboratorio: this.item.laboratorio,
                enderecoLaboratorio: this.item.enderecoLaboratorio,
                numeroEnderecoLaboratorio: this.item.numeroEnderecoLaboratorio,
                bairroLaboratorio: this.item.bairroLaboratorio,
                tipoLaboratorio: this.item.tipoLaboratorio,
                cepLaboratorio: this.item.cepLaboratorio,
                telefoneLaboratorio: this.item.telefoneLaboratorio,
                logotipo: this.item.logotipo,
                nomePrefeitura: this.item.nomePrefeitura,
                ramoAtividade: this.item.ramoAtividade,
                mensagemProtocolo: this.item.mensagemProtocolo,
                sistemaNome: this.item.sistemaNome,
                sistemaVersao: this.item.sistemaVersao,
                usuario: this.item.usuario,
                usuarioNomeSocial: this.item.usuarioNomeSocial ? this.item.usuarioNomeSocial : null,
                urgente: this.item.urgente,
                dataEntrega: this.item.dataEntrega,
            }

            this.print().agendamento(agendamento)
        },
        redirect(item) {

            if (this.origem === 'coleta') {

                this.pacienteId = item.id

                this.$nextTick().then(() => this.$refs.ModalAgendamentoExameLaboratorial.open())
            } else {

                if (this.isModal) {
                    this.$emit('create', item.id)
                } else {
                    this.$router.push({ name: 'exame-laboratorial-agendamento-exame-create', params: { id: item.id } })
                }
            }
        },
        handleAgendado() {
            this.$emit('exit')
        },
    },
    mounted () {
        this.$nextTick(function () {

            if (this.item && this.item.agendamentoNumero) {

                this.showSaveNotification()

                if (this.item.impressaoAgendamentoTipoId == this.enums.impressaoAgendamento.PROTOCOLO) {
                    this.handlePrint()
                } else if (this.item.impressaoAgendamentoTipoId == this.enums.impressaoAgendamento.ETIQUETA) {
                    this.handleEtiquetaPrint()
                } else {
                    this.handlePrint()
                    this.handleEtiquetaPrint()
                }
            }
        })
    },
    mixins: [ repository, error, printExameLaboratorial, stringCase ],
    props: {
        item: { type: Object, default: null },
        origem: { type: String, default: null },
        isModal: { type: Boolean },
        hasAmbulatorio: { type: Boolean, default: false },
    },
}
</script>

<style scoped>
.danger {
    color: var(--danger);
}
.warning {
    color: var(--warning);
}
</style>